import { NextPageContext } from "next";
import { ParsedUrlQuery } from "querystring";

import getSearchResults from "api/requests/getSearchResults";
import getShopResults from "api/requests/getShopResults";
import { Brand } from "custom-types/Brand";
import { Coordinates } from "custom-types/Coordinates";
import { Dispensary } from "custom-types/Dispensary";
import { MappedFilterOption } from "custom-types/Shop";
import { Strain } from "custom-types/Strain";
import { AppStore } from "redux/store";

export const MAX_DISTANCE = 30; // 30 miles

export const generateShopQuery = ({
  q,
  shopCategory,
  shopName,
  product_category,
  strain_category,
  strain_name,
  type,
  deal_active,
}: ParsedUrlQuery) => {
  const query: ParsedUrlQuery = { q };

  if (shopCategory && shopName) {
    query[`filter[${shopCategory}_name]`] =
      typeof shopName === "string" ? [shopName] : shopName;
  }

  if (product_category) {
    query["filter[product_category]"] = String(product_category);
  }

  if (strain_category) {
    query["filter[strain_category]"] = String(strain_category);
  }

  if (strain_name) {
    query["filter[strain_name]"] = String(strain_name);
  }

  if (type) {
    query["filter[type]"] = String(type);
  }

  if (deal_active) {
    query["filter[deal_active]"] = "true";
  }

  return query;
};

export type ShopState = {
  loading: boolean;
  selection?: Brand | Strain;
  dispensary: Dispensary[];
  availableFilters: MappedFilterOption[];
  totalCount?: number;
};

export type ShopResults = Omit<ShopState, "loading">;

export const initialState: ShopState = {
  availableFilters: [],
  dispensary: [],
  loading: false,
  selection: undefined,
  totalCount: 0,
};

export const SHOP_LOADING = "SHOP_LOADING";
export const SHOP_RESULTS = "SHOP_RESULTS";

export type ShopLoadingAction = {
  type: typeof SHOP_LOADING;
};

export type ShopResultsAction = {
  type: typeof SHOP_RESULTS;
  results: ShopResults;
};

export type ShopActions = ShopLoadingAction | ShopResultsAction;

export const getShopState = async (
  ctx: NextPageContext<AppStore>,
  coordinates: Coordinates,
  countryCode: string
) => {
  const {
    store: { dispatch },
    query,
  } = ctx;

  const {
    q,
    shopCategory,
    shopName,
    product_category,
    strain_category,
    strain_name,
    deal_active,
    showSimilar,
  } = query;

  const showSimilarStrains = shopCategory === "strain" && showSimilar === "on";

  const selectionQuery = generateShopQuery({
    shopCategory,
    shopName,
    type: shopCategory,
  });

  const resultsQuery = generateShopQuery({
    deal_active,
    product_category,
    q,
    shopCategory,
    shopName,
    strain_category,
    strain_name,
    type: "menu_item",
  });

  // Only make the request if the user has a brand or strain selected
  const selectionRequest =
    (shopCategory === "brand" || shopCategory === "strain") && shopName
      ? getSearchResults(
          selectionQuery,
          coordinates,
          MAX_DISTANCE,
          countryCode,
          1,
          0,
          "",
          true
        )
      : undefined;

  const [selectionResults, results] = await Promise.all([
    selectionRequest,
    getShopResults(
      resultsQuery,
      coordinates,
      MAX_DISTANCE,
      countryCode,
      showSimilarStrains
    ),
  ]);

  const selection = selectionResults
    ? selectionResults[shopCategory as "brand" | "strain"][0]
    : undefined;

  dispatch<ShopResultsAction>({
    results: { ...results, selection },
    type: SHOP_RESULTS,
  });

  // Forward to the brand page if no results found nearby
  if (
    shopCategory === "brand" &&
    results.totalCount === 0 &&
    ctx?.res &&
    selection?.slug
  ) {
    ctx.res.writeHead(302, {
      Location: `/brands/${selection.slug}`,
    });
    ctx.res.end();
  }
};

export const shopReducer = (
  state = initialState,
  action: ShopActions
): ShopState => {
  switch (action.type) {
    case SHOP_LOADING:
      return {
        ...state,
        loading: true,
      };
    case SHOP_RESULTS:
      return {
        ...state,
        loading: false,
        ...action.results,
      };
    default:
      return state;
  }
};

export default shopReducer;
